import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { useItemMastersQuery } from 'api/useQueries/WarehouseQueries';
import { Combobox } from 'shadcn/components/ui/Combobox';
import { useDebouncedValue } from 'shared';

import { DEFAULT_MAX_OPTIONS_RENDERED, filterAndLimitOptions, normalizeSearchText } from '../utils/itemLookupUtils';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

export function ItemMasterPicker({
    debounceMs = 150,
    enabled = true,
    externalSkuFilter,
    label = 'Item Master SKU',
    maxOptionsRendered = DEFAULT_MAX_OPTIONS_RENDERED,
    onValueChange,
    showLabel = true,
    value = '',
}) {
    const labelId = useId();
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebouncedValue(searchTerm, debounceMs);

    const prevValueRef = useRef(String(value ?? ''));
    useEffect(() => {
        const nextValue = String(value ?? '');
        const prevValue = prevValueRef.current;
        prevValueRef.current = nextValue;
        if (!nextValue && prevValue && searchTerm) {
            setSearchTerm('');
        }
    }, [searchTerm, value]);

    const {
        data: itemMasters = [],
        isFetching,
        isLoading,
    } = useItemMastersQuery({
        activeOnly: true,
        enabled,
        mmOnly: true,
    });

    const filteredItemMasters = useMemo(() => {
        const filterValue = String(externalSkuFilter ?? '');
        if (!filterValue) return itemMasters;
        return itemMasters.filter((row) => String(row.externalSku) === filterValue);
    }, [externalSkuFilter, itemMasters]);

    const baseOptions = useMemo(() => {
        const options = filteredItemMasters.map((row) => {
            const sku = String(row.sku);
            const name = String(row.itemNameInternal ?? '');
            const displayLabel = `SKU ${sku}: ${name}`;
            const searchText = normalizeSearchText(`${sku} ${name}`);
            return { label: displayLabel, searchText, value: sku };
        });
        options.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
        return options;
    }, [filteredItemMasters]);

    const options = useMemo(
        () =>
            filterAndLimitOptions(baseOptions, debouncedSearch, maxOptionsRendered).map(
                ({ label: optLabel, value: optValue }) => ({
                    label: optLabel,
                    value: optValue,
                }),
            ),
        [baseOptions, debouncedSearch, maxOptionsRendered],
    );

    const showLoading = isLoading || (isFetching && baseOptions.length === 0);

    if (showLoading) {
        return (
            <div className="flex flex-col gap-2">
                {showLabel && label && (
                    <span id={labelId} className={labelClassName}>
                        {label}
                    </span>
                )}
                <div className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading item masters…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {showLabel && label && (
                <span id={labelId} className={labelClassName}>
                    {label}
                </span>
            )}
            <Combobox
                id={labelId}
                options={options}
                value={String(value ?? '')}
                handleSelect={(nextValue) => onValueChange(String(nextValue ?? ''))}
                placeholder="Select item master"
                disabled={!options.length}
                popoverWidth="trigger"
                className="w-full"
                triggerClassName="w-full"
                truncateLabel
                onSearchValueChange={setSearchTerm}
                commandProps={{
                    filter: () => 1,
                }}
            />
        </div>
    );
}

ItemMasterPicker.displayName = 'ItemMasterPicker';
